import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton, Typography, Paper, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  FitScreen,
  AspectRatio,
  TextFields,
  CheckBox,
  CalendarToday,
  Draw,
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { PREDEFINED_FIELDS } from '../types';
import ToothSpinner from './ToothSpinner';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface PlacedField {
  id: string;
  tabLabel: string;
  tabType: string;
  pageNumber: number;
  xPosition: number;
  yPosition: number;
  width: number;
  height: number;
  fontSize?: number;
  locked?: boolean;
  // Radio button specific
  radioGroupName?: string;
  radioValue?: string;
}

interface PDFCanvasProps {
  pdfBase64: string;
  selectedFieldType?: string | null;
  fieldDefaults?: { width: number; height: number; fontSize: number };
  placedFields?: PlacedField[];
  selectedFieldId?: string | null;
  onFieldPlace?: (field: Omit<PlacedField, 'id'>) => void;
  onFieldUpdate?: (id: string, updates: Partial<PlacedField>) => void;
  onFieldSelect?: (id: string | null) => void;
  onFieldDelete?: (id: string) => void;
  onCanvasClick?: (x: number, y: number, pageNumber: number) => void;
  renderFieldOverlay?: (pageNumber: number) => React.ReactNode;
}

type ZoomMode = 'custom' | 'fitWidth' | 'fitPage';

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

const PDFCanvas: React.FC<PDFCanvasProps> = ({
  pdfBase64,
  selectedFieldType,
  fieldDefaults,
  placedFields = [],
  selectedFieldId,
  onFieldPlace,
  onFieldUpdate,
  onFieldSelect,
  onFieldDelete,
  onCanvasClick,
  renderFieldOverlay,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1.0);
  const [zoomMode, setZoomMode] = useState<ZoomMode>('fitWidth');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [renderedPages, setRenderedPages] = useState<Map<number, HTMLCanvasElement>>(new Map());
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; pageNumber: number; pdfX: number; pdfY: number } | null>(null);
  
  // Field dragging state
  const [draggingField, setDraggingField] = useState<{ id: string; startX: number; startY: number; fieldStartX: number; fieldStartY: number } | null>(null);
  
  // Field resizing state
  const [resizingField, setResizingField] = useState<{ id: string; handle: ResizeHandle; startX: number; startY: number; fieldStartX: number; fieldStartY: number; fieldStartWidth: number; fieldStartHeight: number } | null>(null);

  // Load PDF document
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        // Convert base64 to Uint8Array
        const binaryString = atob(pdfBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;
        
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF document');
        setLoading(false);
      }
    };

    if (pdfBase64) {
      loadPDF();
    }
  }, [pdfBase64]);

  // Render a single page
  const renderPage = useCallback(async (pageNum: number, scale: number): Promise<HTMLCanvasElement> => {
    if (!pdfDocument) {
      throw new Error('PDF document not loaded');
    }

    const page = await pdfDocument.getPage(pageNum);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to get canvas context');
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;

    return canvas;
  }, [pdfDocument]);

  // Calculate zoom based on mode
  const calculateZoom = useCallback((mode: ZoomMode, containerWidth: number, pageWidth: number, pageHeight: number): number => {
    if (mode === 'fitWidth') {
      return (containerWidth - 40) / pageWidth; // 40px for padding
    } else if (mode === 'fitPage') {
      const containerHeight = containerRef.current?.clientHeight || 600;
      const widthScale = (containerWidth - 40) / pageWidth;
      const heightScale = (containerHeight - 100) / pageHeight; // 100px for controls
      return Math.min(widthScale, heightScale);
    }
    return zoom;
  }, [zoom]);

  // Render all pages when PDF or zoom changes
  useEffect(() => {
    const renderAllPages = async () => {
      if (!pdfDocument || !containerRef.current) return;

      try {
        const newRenderedPages = new Map<number, HTMLCanvasElement>();
        
        // Get first page to calculate dimensions
        const firstPage = await pdfDocument.getPage(1);
        const baseViewport = firstPage.getViewport({ scale: 1.0 });
        
        // Calculate zoom if in auto mode
        let actualZoom = zoom;
        if (zoomMode !== 'custom') {
          actualZoom = calculateZoom(zoomMode, containerRef.current.clientWidth, baseViewport.width, baseViewport.height);
          setZoom(actualZoom);
        }

        // Render all pages
        for (let i = 1; i <= pdfDocument.numPages; i++) {
          const canvas = await renderPage(i, actualZoom);
          newRenderedPages.set(i, canvas);
        }

        setRenderedPages(newRenderedPages);
      } catch (err) {
        console.error('Error rendering pages:', err);
        setError('Failed to render PDF pages');
      }
    };

    renderAllPages();
  }, [pdfDocument, zoom, zoomMode, renderPage, calculateZoom]);

  // Handle zoom in
  const handleZoomIn = () => {
    setZoomMode('custom');
    setZoom(prev => Math.min(prev * 1.2, 5.0));
  };

  // Handle zoom out
  const handleZoomOut = () => {
    setZoomMode('custom');
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  };

  // Handle fit to width
  const handleFitWidth = () => {
    setZoomMode('fitWidth');
  };

  // Handle fit to page
  const handleFitPage = () => {
    setZoomMode('fitPage');
  };

  // Handle canvas click for field placement
  const handleCanvasClick = (event: React.MouseEvent<HTMLDivElement>, pageNumber: number) => {
    // Prevent if panning or dragging/resizing
    if (isPanning || draggingField || resizingField) return;

    const canvas = renderedPages.get(pageNumber);
    if (!canvas) return;

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Convert screen coordinates to PDF points (1/72 inch)
    const pdfX = x / zoom;
    const pdfY = y / zoom;

    // Check if clicking on an existing field
    const clickedField = placedFields.find(field => {
      if (field.pageNumber !== pageNumber) return false;
      return (
        pdfX >= field.xPosition &&
        pdfX <= field.xPosition + field.width &&
        pdfY >= field.yPosition &&
        pdfY <= field.yPosition + field.height
      );
    });

    if (clickedField) {
      // Select the field
      onFieldSelect?.(clickedField.id);
      return;
    }

    // If a field type is selected, place it
    if (selectedFieldType && fieldDefaults && onFieldPlace) {
      onFieldPlace({
        tabLabel: '', // Will be set by parent
        tabType: selectedFieldType,
        pageNumber,
        xPosition: pdfX,
        yPosition: pdfY,
        width: fieldDefaults.width,
        height: fieldDefaults.height,
        fontSize: fieldDefaults.fontSize,
      });
      return;
    }

    // Otherwise show context menu
    if (!selectedFieldType) {
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        pageNumber,
        pdfX,
        pdfY,
      });
    }

    // Deselect any selected field
    onFieldSelect?.(null);

    // Call legacy handler if provided
    if (onCanvasClick) {
      onCanvasClick(pdfX, pdfY, pageNumber);
    }
  };

  // Handle context menu close
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Count fields by tabLabel
  const countFieldsByLabel = (tabLabel: string): number => {
    let count = 0;
    placedFields.forEach(field => {
      if (field.tabLabel === tabLabel) {
        count++;
      }
    });
    return count;
  };

  // Handle context menu field selection
  const handleContextMenuFieldSelect = (fieldType: string, defaults: { width: number; height: number; fontSize: number }, tabLabel?: string, locked?: boolean) => {
    if (!contextMenu || !onFieldPlace) return;

    onFieldPlace({
      tabLabel: tabLabel || '', // Use provided tabLabel or empty string
      tabType: fieldType,
      pageNumber: contextMenu.pageNumber,
      xPosition: contextMenu.pdfX,
      yPosition: contextMenu.pdfY,
      width: defaults.width,
      height: defaults.height,
      fontSize: defaults.fontSize,
      locked,
    });

    setContextMenu(null);
  };

  // Handle field drag start
  const handleFieldMouseDown = (event: React.MouseEvent, field: PlacedField) => {
    event.stopPropagation();
    
    // Select the field
    onFieldSelect?.(field.id);

    // Check if clicking on a resize handle
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const handleSize = 8;
    const fieldWidth = field.width * zoom;
    const fieldHeight = field.height * zoom;

    // Check corners first
    if (x < handleSize && y < handleSize) {
      setResizingField({
        id: field.id,
        handle: 'nw',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }
    if (x > fieldWidth - handleSize && y < handleSize) {
      setResizingField({
        id: field.id,
        handle: 'ne',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }
    if (x < handleSize && y > fieldHeight - handleSize) {
      setResizingField({
        id: field.id,
        handle: 'sw',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }
    if (x > fieldWidth - handleSize && y > fieldHeight - handleSize) {
      setResizingField({
        id: field.id,
        handle: 'se',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }

    // Check edges
    if (y < handleSize) {
      setResizingField({
        id: field.id,
        handle: 'n',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }
    if (y > fieldHeight - handleSize) {
      setResizingField({
        id: field.id,
        handle: 's',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }
    if (x < handleSize) {
      setResizingField({
        id: field.id,
        handle: 'w',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }
    if (x > fieldWidth - handleSize) {
      setResizingField({
        id: field.id,
        handle: 'e',
        startX: event.clientX,
        startY: event.clientY,
        fieldStartX: field.xPosition,
        fieldStartY: field.yPosition,
        fieldStartWidth: field.width,
        fieldStartHeight: field.height,
      });
      return;
    }

    // Otherwise, start dragging
    setDraggingField({
      id: field.id,
      startX: event.clientX,
      startY: event.clientY,
      fieldStartX: field.xPosition,
      fieldStartY: field.yPosition,
    });
  };

  // Handle mouse move for dragging and resizing
  const handleMouseMoveGlobal = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggingField && onFieldUpdate) {
      const deltaX = (event.clientX - draggingField.startX) / zoom;
      const deltaY = (event.clientY - draggingField.startY) / zoom;

      onFieldUpdate(draggingField.id, {
        xPosition: Math.max(0, draggingField.fieldStartX + deltaX),
        yPosition: Math.max(0, draggingField.fieldStartY + deltaY),
      });
    } else if (resizingField && onFieldUpdate) {
      const deltaX = (event.clientX - resizingField.startX) / zoom;
      const deltaY = (event.clientY - resizingField.startY) / zoom;

      const updates: Partial<PlacedField> = {};

      switch (resizingField.handle) {
        case 'nw':
          updates.xPosition = Math.max(0, resizingField.fieldStartX + deltaX);
          updates.yPosition = Math.max(0, resizingField.fieldStartY + deltaY);
          updates.width = Math.max(20, resizingField.fieldStartWidth - deltaX);
          updates.height = Math.max(10, resizingField.fieldStartHeight - deltaY);
          break;
        case 'ne':
          updates.yPosition = Math.max(0, resizingField.fieldStartY + deltaY);
          updates.width = Math.max(20, resizingField.fieldStartWidth + deltaX);
          updates.height = Math.max(10, resizingField.fieldStartHeight - deltaY);
          break;
        case 'sw':
          updates.xPosition = Math.max(0, resizingField.fieldStartX + deltaX);
          updates.width = Math.max(20, resizingField.fieldStartWidth - deltaX);
          updates.height = Math.max(10, resizingField.fieldStartHeight + deltaY);
          break;
        case 'se':
          updates.width = Math.max(20, resizingField.fieldStartWidth + deltaX);
          updates.height = Math.max(10, resizingField.fieldStartHeight + deltaY);
          break;
        case 'n':
          updates.yPosition = Math.max(0, resizingField.fieldStartY + deltaY);
          updates.height = Math.max(10, resizingField.fieldStartHeight - deltaY);
          break;
        case 's':
          updates.height = Math.max(10, resizingField.fieldStartHeight + deltaY);
          break;
        case 'w':
          updates.xPosition = Math.max(0, resizingField.fieldStartX + deltaX);
          updates.width = Math.max(20, resizingField.fieldStartWidth - deltaX);
          break;
        case 'e':
          updates.width = Math.max(20, resizingField.fieldStartWidth + deltaX);
          break;
      }

      onFieldUpdate(resizingField.id, updates);
    } else if (isPanning && containerRef.current) {
      const newX = event.clientX - panStart.x;
      const newY = event.clientY - panStart.y;
      setScrollPosition({ x: newX, y: newY });
      containerRef.current.scrollLeft = -newX;
      containerRef.current.scrollTop = -newY;
    }
  };

  // Handle pan start
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // Don't pan if dragging or resizing fields
    if (draggingField || resizingField) return;
    
    if (event.button === 0 && zoom > 1.0) { // Left mouse button and zoomed in
      setIsPanning(true);
      setPanStart({ x: event.clientX - scrollPosition.x, y: event.clientY - scrollPosition.y });
      event.preventDefault();
    }
  };

  // Handle pan move
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    handleMouseMoveGlobal(event);
  };

  // Handle pan end
  const handleMouseUp = () => {
    setIsPanning(false);
    setDraggingField(null);
    setResizingField(null);
  };

  // Handle keyboard events for field deletion
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't delete fields when typing in input elements
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedFieldId && onFieldDelete) {
          event.preventDefault();
          onFieldDelete(selectedFieldId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFieldId, onFieldDelete]);

  // Update current page based on scroll position
  const handleScroll = () => {
    if (!containerRef.current) return;

    const scrollTop = containerRef.current.scrollTop;
    let accumulatedHeight = 0;
    
    for (let i = 1; i <= totalPages; i++) {
      const canvas = renderedPages.get(i);
      if (!canvas) continue;
      
      accumulatedHeight += canvas.height + 20; // 20px gap between pages
      
      if (scrollTop < accumulatedHeight) {
        setCurrentPage(i);
        break;
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <ToothSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Paper sx={{ p: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleZoomOut} size="small" title="Zoom Out">
            <ZoomOut />
          </IconButton>
          <IconButton onClick={handleZoomIn} size="small" title="Zoom In">
            <ZoomIn />
          </IconButton>
          <IconButton onClick={handleFitWidth} size="small" title="Fit to Width">
            <AspectRatio />
          </IconButton>
          <IconButton onClick={handleFitPage} size="small" title="Fit to Page">
            <FitScreen />
          </IconButton>
        </Box>
        
        <Typography variant="body2" sx={{ ml: 'auto' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        
        <Typography variant="body2">
          Page {currentPage} of {totalPages}
        </Typography>
      </Paper>

      {/* PDF Canvas Container */}
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          cursor: isPanning ? 'grabbing' : draggingField ? 'move' : resizingField ? 'nwse-resize' : zoom > 1.0 ? 'grab' : selectedFieldType ? 'crosshair' : 'default',
          backgroundColor: '#525659',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
            const canvas = renderedPages.get(pageNum);
            if (!canvas) return null;

            const pageFields = placedFields.filter(f => f.pageNumber === pageNum);

            return (
              <Box
                key={`page-${pageNum}`}
                sx={{
                  position: 'relative',
                  boxShadow: 3,
                  backgroundColor: 'white',
                }}
              >
                {/* PDF Page Canvas */}
                <Box
                  onClick={(e) => handleCanvasClick(e, pageNum)}
                  sx={{
                    position: 'relative',
                    cursor: selectedFieldType ? 'crosshair' : 'default',
                  }}
                >
                  <canvas
                    ref={(canvasEl) => {
                      if (canvasEl && canvas) {
                        // Copy the rendered canvas content to this canvas element
                        canvasEl.width = canvas.width;
                        canvasEl.height = canvas.height;
                        const ctx = canvasEl.getContext('2d');
                        if (ctx) {
                          ctx.drawImage(canvas, 0, 0);
                        }
                      }
                    }}
                    style={{ display: 'block' }}
                  />
                </Box>

                {/* Field Overlay Layer */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: canvas.width,
                    height: canvas.height,
                    pointerEvents: 'none',
                  }}
                >
                  {/* Render placed fields */}
                  {pageFields.map((field) => {
                    const isSelected = field.id === selectedFieldId;
                    const isRadioButton = field.tabType === 'radioGroupTabs';
                    
                    return (
                      <Box
                        key={field.id}
                        data-field-id={field.id}
                        sx={{
                          position: 'absolute',
                          left: field.xPosition * zoom,
                          top: field.yPosition * zoom,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          pointerEvents: 'none',
                        }}
                      >
                        {/* Field box */}
                        <Box
                          onMouseDown={(e) => handleFieldMouseDown(e, field)}
                          sx={{
                            width: field.width * zoom,
                            height: field.height * zoom,
                            border: isSelected ? '2px solid #1976d2' : '1px solid #666',
                            backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.3)',
                            cursor: 'move',
                            pointerEvents: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: (field.fontSize || 12) * zoom,
                            overflow: 'hidden',
                            position: 'relative',
                            borderRadius: isRadioButton ? '50%' : 0,
                            '&:hover': {
                              backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.15)' : 'rgba(255, 255, 255, 0.5)',
                              borderColor: isSelected ? '#1976d2' : '#333',
                            },
                          }}
                        >
                          {/* Field label (inside for non-radio fields) */}
                          {!isRadioButton && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: Math.min(10 * zoom, field.height * zoom * 0.6),
                                color: '#333',
                                fontWeight: isSelected ? 'bold' : 'normal',
                                userSelect: 'none',
                                pointerEvents: 'none',
                              }}
                            >
                              {field.tabLabel || field.tabType}
                            </Typography>
                          )}

                          {/* Resize handles (only for selected field) */}
                          {isSelected && (
                            <>
                              {/* Corner handles */}
                              <Box sx={{ position: 'absolute', top: -4, left: -4, width: 8, height: 8, backgroundColor: '#1976d2', cursor: 'nw-resize', pointerEvents: 'auto' }} />
                              <Box sx={{ position: 'absolute', top: -4, right: -4, width: 8, height: 8, backgroundColor: '#1976d2', cursor: 'ne-resize', pointerEvents: 'auto' }} />
                              <Box sx={{ position: 'absolute', bottom: -4, left: -4, width: 8, height: 8, backgroundColor: '#1976d2', cursor: 'sw-resize', pointerEvents: 'auto' }} />
                              <Box sx={{ position: 'absolute', bottom: -4, right: -4, width: 8, height: 8, backgroundColor: '#1976d2', cursor: 'se-resize', pointerEvents: 'auto' }} />
                              
                              {/* Edge handles */}
                              <Box sx={{ position: 'absolute', top: -4, left: '50%', transform: 'translateX(-50%)', width: 8, height: 8, backgroundColor: '#1976d2', cursor: 'n-resize', pointerEvents: 'auto' }} />
                              <Box sx={{ position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)', width: 8, height: 8, backgroundColor: '#1976d2', cursor: 's-resize', pointerEvents: 'auto' }} />
                              <Box sx={{ position: 'absolute', left: -4, top: '50%', transform: 'translateY(-50%)', width: 8, height: 8, backgroundColor: '#1976d2', cursor: 'w-resize', pointerEvents: 'auto' }} />
                              <Box sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)', width: 8, height: 8, backgroundColor: '#1976d2', cursor: 'e-resize', pointerEvents: 'auto' }} />
                            </>
                          )}
                        </Box>

                        {/* Radio button label (outside, next to the circle) */}
                        {isRadioButton && field.radioValue && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10 * zoom,
                              color: '#333',
                              fontWeight: isSelected ? 'bold' : 'normal',
                              userSelect: 'none',
                              pointerEvents: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {field.radioValue}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}

                  {/* Legacy field overlay */}
                  {renderFieldOverlay && renderFieldOverlay(pageNum)}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.y, left: contextMenu.x }
              : undefined
          }
          PaperProps={{
            sx: {
              maxWidth: 800,
              minWidth: 700,
            }
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
            {/* Column 1: Antragsteller */}
            <Box>
              <MenuItem disabled sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <Typography variant="subtitle2">Antragsteller</Typography>
              </MenuItem>
              {PREDEFINED_FIELDS.filter(f => f.category === 'antragsteller').map((field) => {
                const count = countFieldsByLabel(field.tabLabel);
                return (
                  <MenuItem
                    key={field.tabLabel}
                    onClick={() =>
                      handleContextMenuFieldSelect('textTabs', {
                        width: field.defaultWidth,
                        height: field.defaultHeight,
                        fontSize: field.defaultFontSize,
                      }, field.tabLabel, field.defaultLocked)
                    }
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={
                        <span>
                          {field.displayName}{' '}
                          <span style={{ fontWeight: count === 0 ? 'bold' : 'normal', color: count === 0 ? 'red' : 'inherit' }}>
                            [{count}]
                          </span>
                        </span>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </MenuItem>
                );
              })}
            </Box>

            {/* Column 2: Versicherte Person */}
            <Box>
              <MenuItem disabled sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <Typography variant="subtitle2">Versicherte Person</Typography>
              </MenuItem>
              {PREDEFINED_FIELDS.filter(f => f.category === 'versicherte').map((field) => {
                const count = countFieldsByLabel(field.tabLabel);
                return (
                  <MenuItem
                    key={field.tabLabel}
                    onClick={() =>
                      handleContextMenuFieldSelect('textTabs', {
                        width: field.defaultWidth,
                        height: field.defaultHeight,
                        fontSize: field.defaultFontSize,
                      }, field.tabLabel, field.defaultLocked)
                    }
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={
                        <span>
                          {field.displayName}{' '}
                          <span style={{ fontWeight: count === 0 ? 'bold' : 'normal', color: count === 0 ? 'red' : 'inherit' }}>
                            [{count}]
                          </span>
                        </span>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </MenuItem>
                );
              })}
            </Box>

            {/* Column 3: Tarif Daten */}
            <Box>
              <MenuItem disabled sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <Typography variant="subtitle2">Tarif Daten</Typography>
              </MenuItem>
              {PREDEFINED_FIELDS.filter(f => f.category === 'tarif').map((field) => {
                const count = countFieldsByLabel(field.tabLabel);
                return (
                  <MenuItem
                    key={field.tabLabel}
                    onClick={() =>
                      handleContextMenuFieldSelect('textTabs', {
                        width: field.defaultWidth,
                        height: field.defaultHeight,
                        fontSize: field.defaultFontSize,
                      }, field.tabLabel, field.defaultLocked)
                    }
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={
                        <span>
                          {field.displayName}{' '}
                          <span style={{ fontWeight: count === 0 ? 'bold' : 'normal', color: count === 0 ? 'red' : 'inherit' }}>
                            [{count}]
                          </span>
                        </span>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </MenuItem>
                );
              })}
            </Box>

            {/* Column 4: Felder */}
            <Box>
              <MenuItem disabled sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <Typography variant="subtitle2">Felder</Typography>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleContextMenuFieldSelect('textTabs', {
                    width: 150,
                    height: 20,
                    fontSize: 12,
                  })
                }
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <TextFields fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Text Field"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleContextMenuFieldSelect('checkboxTabs', {
                    width: 20,
                    height: 20,
                    fontSize: 12,
                  })
                }
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckBox fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Checkbox"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleContextMenuFieldSelect('dateSignedTabs', {
                    width: 100,
                    height: 20,
                    fontSize: 12,
                  })
                }
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CalendarToday fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Date Signed"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleContextMenuFieldSelect('signHereTabs', {
                    width: 100,
                    height: 40,
                    fontSize: 12,
                  })
                }
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Draw fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Signature"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </MenuItem>
            </Box>
          </Box>
        </Menu>
      </Box>
    </Box>
  );
};

export default PDFCanvas;
